import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Search, Store, User, LogOut, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useEffect, useState } from "react";

export default function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setQ(params.get("q") || "");
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/browse?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header
      className="glass-nav sticky top-0 z-40 border-b border-border/60"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-baseline gap-0 shrink-0">
          <span className="font-display text-2xl font-semibold tracking-tight text-primary leading-none">
            VintedStyle
          </span>
          <span className="font-display text-2xl font-medium text-foreground leading-none">.be</span>
        </Link>

        <form onSubmit={onSearch} className="flex-1 max-w-xl hidden md:flex ml-6">
          <div className="relative w-full">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("nav.search_placeholder")}
              data-testid="header-search-input"
              className="w-full pl-10 pr-4 h-11 rounded-full bg-muted/60 border border-transparent focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/25 text-sm transition-colors"
            />
          </div>
        </form>

        <nav className="ml-auto flex items-center gap-1 sm:gap-2">
          <NavLink to="/browse" data-testid="nav-browse" end={false}
            className={({ isActive }) =>
              `hidden md:inline-flex items-center gap-1.5 h-10 px-3 rounded-full text-sm font-medium hover:bg-accent transition-colors ${
                isActive ? "text-primary" : "text-foreground"
              }`
            }>
            <Store className="w-4 h-4" /> {t("nav.browse")}
          </NavLink>

          <LanguageSwitcher />

          {user && user.id ? (
            <>
              <NavLink to="/sell" data-testid="nav-sell">
                {({ isActive }) => (
                  <Button size="sm" className={`rounded-full h-10 px-4 gap-1.5 ${isActive ? "" : ""}`}>
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">{t("nav.sell")}</span>
                  </Button>
                )}
              </NavLink>

              <NavLink to="/favorites" data-testid="nav-favorites"
                className={({ isActive }) =>
                  `hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-accent transition-colors ${
                    isActive ? "text-primary" : ""
                  }`
                }>
                <Heart className="w-5 h-5" />
              </NavLink>

              <NavLink to="/messages" data-testid="nav-messages"
                className={({ isActive }) =>
                  `inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-accent transition-colors ${
                    isActive ? "text-primary" : ""
                  }`
                }>
                <MessageCircle className="w-5 h-5" />
              </NavLink>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-profile"
                    className="w-10 h-10 rounded-full bg-accent hover:bg-secondary transition-colors flex items-center justify-center font-medium text-sm text-accent-foreground"
                  >
                    {(user.name || "U").slice(0, 1).toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-sm font-medium">{user.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" /> {t("nav.profile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/favorites")} data-testid="menu-favorites">
                    <Heart className="w-4 h-4 mr-2" /> {t("nav.favorites")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden sm:inline-flex">
                <Button variant="ghost" size="sm" className="rounded-full h-10 px-4 font-medium">
                  {t("nav.login")}
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button size="sm" className="rounded-full h-10 px-4 font-medium">
                  {t("nav.register")}
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>

      {/* Mobile search */}
      <form onSubmit={onSearch} className="md:hidden max-w-7xl mx-auto px-4 pb-3">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("nav.search_placeholder_mobile")}
            data-testid="header-search-input-mobile"
            className="w-full pl-10 pr-4 h-11 rounded-full bg-muted/60 border border-transparent focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/25 text-sm"
          />
        </div>
      </form>
    </header>
  );
}
